import { BillingActionRequestItem } from '@/lib/api/financial/billing-action-request-api'
import { useNavigate } from 'react-router-dom'
import {
    MdCancel,
    MdCheck,
    MdClose,
    MdDelete,
    MdEvent,
    MdPaid,
    MdPriceCheck,
} from 'react-icons/md'
import ApprovalCard from './approval-card'
import CompanyBadge from './company-badge'
import ReceiptReview from './receipt-review'
import { formatCurrency, formatDate } from '../format'

const ACTION_LABELS: Record<BillingActionRequestItem['action'], string> = {
    MARK_PAID: 'Marcar como pago',
    CANCEL: 'Cancelar cobrança',
    SETTLE_PROCEDURE: 'Quitar com desconto',
}

interface ActionRequestCardProps {
    item: BillingActionRequestItem
    selectable: boolean
    selected: boolean
    onToggleSelect: () => void
    canApprove: boolean
    /** Excluir tira o pedido da fila sem decidir — exige `delete` na role. */
    canDelete?: boolean
    onDelete?: () => void
    onApprove: () => void
    onReject: () => void
    onOpenReceipt?: () => void
}

/**
 * Ação sensível (marcar como pago / cancelar) aguardando aprovação.
 *
 * Não movimenta a conta digital — por isso quem aprova é quem tem permissão de
 * editar as aprovações, sem senha e sem precisar ser operador financeiro eleito.
 * O que decide aqui é o comprovante, que ganha destaque no corpo do card.
 */
export default function ActionRequestCard({
    item,
    selectable,
    selected,
    onToggleSelect,
    canApprove,
    canDelete,
    onDelete,
    onApprove,
    onReject,
    onOpenReceipt,
}: ActionRequestCardProps) {
    const navigate = useNavigate()
    const isMarkPaid = item.action === 'MARK_PAID'
    const isSettle = item.action === 'SETTLE_PROCEDURE'
    const isPayable = item.billing.type === 'PAYABLE'
    const Icon = isSettle ? MdPriceCheck : isMarkPaid ? MdPaid : MdCancel

    const counterparty =
        (isPayable
            ? item.billing.supplier?.name ||
              item.billing.partner?.tradingName ||
              item.billing.partner?.name
            : item.billing.client?.name ||
              item.billing.partner?.tradingName ||
              item.billing.partner?.name) || null

    return (
        <ApprovalCard
            selectable={selectable}
            selected={selected}
            onToggleSelect={onToggleSelect}
            eyebrow={
                <>
                    <span
                        className={`badge badge-sm gap-1 ${
                            isSettle
                                ? 'badge-warning'
                                : isMarkPaid
                                  ? 'badge-success'
                                  : 'badge-error'
                        }`}
                    >
                        <Icon className="text-sm" />
                        {ACTION_LABELS[item.action]}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <MdEvent className="text-sm" />
                        {formatDate(item.createdAt)}
                    </span>
                    <span>·</span>
                    <span>Por {item.requestedBy?.name || 'sistema'}</span>
                    <CompanyBadge company={item.company} />
                </>
            }
            title={
                <button
                    className="link link-hover text-left hover:text-secondary"
                    onClick={() =>
                        navigate(`/financial/billings/${item.billingId}`)
                    }
                >
                    {item.billing.description || `Lançamento #${item.billingId}`}
                </button>
            }
            subtitle={
                <span className="flex items-center gap-2 flex-wrap">
                    {counterparty}
                    {/*
                     * Quitação: o valor da cobrança não é o que está em jogo. Quem
                     * decide precisa ver quanto vira desconto e quanto de repasse a
                     * aprovação libera — sem isso, aprova no escuro.
                     */}
                    {isSettle && item.payload?.discountAmount != null && (
                        <span className="badge badge-xs badge-warning">
                            Desconto {formatCurrency(item.payload.discountAmount)}
                        </span>
                    )}
                    {isSettle && item.payload?.payoutAmount != null && (
                        <span className="badge badge-xs badge-ghost">
                            Libera {formatCurrency(item.payload.payoutAmount)} de repasse
                        </span>
                    )}
                    <span
                        className={`badge badge-xs ${
                            isPayable ? 'badge-error' : 'badge-success'
                        }`}
                    >
                        {isPayable ? 'A Pagar' : 'A Receber'}
                    </span>
                </span>
            }
            amount={formatCurrency(item.billing.amount)}
            actions={
                canApprove || canDelete ? (
                    <>
                        {canDelete && onDelete && (
                            <button
                                className="btn btn-sm btn-ghost text-error"
                                onClick={onDelete}
                                title="Excluir da fila sem decidir a ação"
                            >
                                <MdDelete /> Excluir
                            </button>
                        )}
                        {canApprove && (
                            <>
                                <button
                                    className="btn btn-sm btn-error btn-outline"
                                    onClick={onReject}
                                >
                                    <MdClose /> Rejeitar
                                </button>
                                <button
                                    className="btn btn-sm btn-success"
                                    onClick={onApprove}
                                >
                                    <MdCheck /> Aprovar
                                </button>
                            </>
                        )}
                    </>
                ) : (
                    <span className="text-xs text-base-content/50">
                        Sem permissão para decidir
                    </span>
                )
            }
        >
            <ReceiptReview
                payload={item.payload}
                hasReceipt={item.billing.hasReceipt}
                onOpenReceipt={onOpenReceipt}
            />
        </ApprovalCard>
    )
}
